import type {State} from 'src/reducers';
import type {
  AtsEntity,
  EntityMappingsBySenseName,
  EntityType,
} from 'src/types/ats-entities';
import type {Workflow} from 'src/types/workflow';
import type {SenseEvent} from 'src/types/events';

import {createSelector} from 'reselect';
import startCase from 'lodash/startCase';
import _get from 'lodash/get';
import keyBy from 'lodash/keyBy';

import {values} from 'src/utils/object';
import {prettyBullhornLabel} from 'src/utils/entities';
import {selectEntityMappingsByName} from 'src/selectors/ats-entities';
import {doesTouchpointBelongToThisAction} from 'src/utils/audience-list';


export const selectCurrentWorkflow = (
  state: State,
): Workflow | null | undefined => {
  const workflowId = state.route.params.workflowId;
  return workflowId ? selectWorkflow(state, workflowId) : null;
};

export const selectWorkflowWithEvents = (
  state: State,
  workflowId: string,
): Workflow | null | undefined =>
  state.workflows.workflowsWithEvents[workflowId];

export const selectCurrentWorkflowWithEvents = (
  state: State,
): Workflow | null | undefined => {
  const workflowId = state.route.params.workflowId;
  return workflowId ? selectWorkflowWithEvents(state, workflowId) : null;
};

// @ts-expect-error - TS2322 - Type 'OutputParametricSelector<any, any, SenseEvent | null | undefined, (res1: any, res2: Workflow | null | undefined, res3: any) => SenseEvent | null | undefined>' is not assignable to type '(state: any, workflowId: string, eventId: string) => SenseEvent'.
export const selectEventById: (
  state: State,
  workflowId: string,
  eventId: string,
) => SenseEvent = createSelector(
  (state) => state,
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'workflowId' implicitly has an 'any' type.
  (state, workflowId) => selectWorkflowWithEvents(state, workflowId),
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter '__' implicitly has an 'any' type. | TS7006 - Parameter 'eventId' implicitly has an 'any' type.
  (_, __, eventId) => eventId,
  (state, workflow, eventId) =>
    eventId && workflow?.events
      ? values(workflow.events).find(({id}) => id === eventId)
      : null,
);

// @ts-expect-error - TS2322 - Type 'OutputSelector<any, SenseEvent | null | undefined, (res1: any, res2: Workflow | null | undefined) => SenseEvent | null | undefined>' is not assignable to type '(state: any) => SenseEvent'.
export const selectCurrentEvent: (state: State) => SenseEvent = createSelector(
  (state) => state,
  selectCurrentWorkflowWithEvents,
  (state, workflow) => {
    const eventId = state.route.params.eventId;
    return eventId && workflow
      ? values(workflow.events).find(({id}) => id === eventId)
      : null;
  },
);

export const selectAllowedEntityTypesByAtsName = (state: State): EntityType[] =>
  state.workflows.entityTypes;

export const selectWorkflow = (
  state: State,
  workflowId: string,
): Workflow | null | undefined => state.workflows.workflows[workflowId];

export const selectWorkflows = (
  state: State,
): {
  [key: string]: Workflow;
} => state.workflows.workflows;

export const selectCleanupWorkflows: (state: State) => Workflow[] =
  createSelector(selectWorkflows, (workflows) =>
    values(workflows).filter((workflow) => workflow.dbCleanup),
  );

//This selector returns only those journeys which are required during abt flows.
export const selectAbtWorkflows: (
  arg1: State,
  arg2?: string | null | undefined,
) => {
  label: string;
  value: string;
}[] = createSelector(
  selectWorkflows,
  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'abtAttributeType' implicitly has an 'any' type.
  (_, abtAttributeType) => abtAttributeType,
  (workflows, abtAttributeType) => {
    const result: Array<{
      label: string;
      value: string;
    }> = [];
    for (const workflowId of Object.keys(workflows)) {
      const eventSummaryList =
        /*Here we are deliberately putting both eventsSummary & events_summary,
                since the workflow selector for Journeys picks workflows from old flux Store and
                for Lists it picks from Redux store. Can be removed when J2 is implemented. */
        // @ts-expect-error - TS2551 - Property 'eventsSummary' does not exist on type 'Workflow'. Did you mean 'events_summary'?
        workflows[workflowId].eventsSummary ??
        workflows[workflowId].events_summary;
      if (
        abtAttributeType &&
        eventSummaryList &&
        Array.isArray(eventSummaryList)
      ) {
        if (
          eventSummaryList.some(
            ({sendType, eventType, send_type, event_type}) =>
              doesTouchpointBelongToThisAction(
                abtAttributeType,
                sendType ?? send_type,
                eventType ?? event_type,
              ),
          )
        ) {
          result.push({
            label: workflows[workflowId].name,
            value: workflows[workflowId].id,
          });
        }
      }
    }
    return result;
  },
);

export const allowedEntityTypesAsMap: (
  state: State,
) => EntityMappingsBySenseName = createSelector(
  selectAllowedEntityTypesByAtsName,
  selectEntityMappingsByName,
  (entityTypes, mappings) =>
    keyBy(
      entityTypes.map((type) => mappings[type]),
      'sense_name',
    ),
);

export const allowedEntityTypesAsSelectValues: (
  state: State,
  workflowId: string,
) => {
  value: string;
  label: string;
}[] = createSelector(
  selectAllowedEntityTypesByAtsName,
  selectEntityMappingsByName,
  (entityTypes, mappings) =>
    entityTypes.map((value) => {
      const label = mappings[value]?.display_name || prettyBullhornLabel(value);
      return {label, value};
    }),
);

export const recipientTypesForWorkflowAsSelectValues: (
  state: State,
  workflowId: string,
) => {
  value: string;
  label: string;
}[] = createSelector(
  // @ts-expect-error - TS7006 - Parameter 'state' implicitly has an 'any' type. | TS7006 - Parameter 'workflowId' implicitly has an 'any' type.
  (state, workflowId) =>
    _get(state, ['workflows', 'recipientTypes', workflowId, 'relatedEntities']),
  (relatedEntities) =>
    relatedEntities
      ? // @ts-expect-error - TS7031 - Binding element 'relatedEntity' implicitly has an 'any' type.
        relatedEntities.map(({relatedEntity}) => ({
          value: relatedEntity,
          label: startCase(relatedEntity),
        }))
      : [],
);

export const getCurrentWorkflowEntityType = (
  state: State,
): AtsEntity | null | undefined => {
  // TODO (kyle): is all this optional chaining required?
  const workflowId = state.route?.params?.workflowId;
  const atsType = state.workflows.workflows[workflowId]?.entityType;
  return state.atsEntities?.entities.find((type: any) => type.name === atsType);
};
